import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro0 } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Layers, Trash2, Upload } from "lucide-react";

export function BudgetVersions({ endpoint, year, rows, canEdit, onLoad }) {
  const [name, setName] = useState("");
  const base = `${endpoint}/versions`;
  const list = useQuery({ queryKey: ["bva-versions", endpoint, year], queryFn: async () => (await api.get(`${base}?year=${year}`)).data });
  const create = useMutation({
    mutationFn: async () => (await api.post(base, { year, name, lines: rows.map((r) => ({ account_code: r.account_code, months: r.budget_months })) })).data,
    onSuccess: (v) => { setName(""); list.refetch(); toast.success(`Αποθηκεύτηκε η έκδοση «${v.name}»`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`${base}/${id}`)).data,
    onSuccess: () => { list.refetch(); toast.success("Η έκδοση διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const load = async (v) => {
    try {
      const d = (await api.get(`${base}/${v.id}`)).data;
      onLoad(d.lines || []);
      toast.success(`Φορτώθηκε η έκδοση «${v.name}» — πάτα Αποθήκευση για να γίνει ο ενεργός προϋπολογισμός`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const versions = list.data || [];
  return (
    <div className="rounded-2xl border border-[#E3DED4] bg-white p-4 space-y-3" data-testid="bva-versions">
      <div className="flex flex-wrap items-center gap-2">
        <Layers className="h-4 w-4 text-[#6E7A63]" />
        <span className="text-sm font-semibold text-[#45423C]">Αποθηκευμένες εκδόσεις {year}</span>
        {canEdit && (
          <div className="ml-auto flex items-center gap-2">
            <input data-testid="bva-version-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="π.χ. Αισιόδοξο, Έκδοση Μαρτίου"
              className="w-[220px] rounded-xl border border-[#E3DED4] px-3 py-1.5 text-sm outline-none focus:border-[#45423C]" />
            <button data-testid="bva-version-save" onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}
              className="rounded-xl bg-[#45423C] text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-50">Αποθήκευση έκδοσης</button>
          </div>
        )}
      </div>
      {versions.length === 0 ? (
        <p className="text-xs text-[#8A8577]" data-testid="bva-versions-empty">Δεν υπάρχουν εκδόσεις. Αποθηκεύει τα ποσά που βλέπετε στον πίνακα με ένα όνομα.</p>
      ) : (
        <div className="divide-y divide-[#E3DED4]">
          {versions.map((v) => (
            <div key={v.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`bva-version-${v.id}`}>
              <span className="font-medium text-[#45423C]">{v.name}</span>
              <span className="text-xs text-[#8A8577]">{(v.created_at || "").slice(0, 16).replace("T", " ")}{v.created_by ? ` · ${v.created_by}` : ""}</span>
              <span className="ml-auto tabular font-semibold text-[#45423C]">{fmtEuro0(v.total)}</span>
              {canEdit && (
                <>
                  <button data-testid={`bva-version-load-${v.id}`} onClick={() => load(v)} title="Φόρτωση στον πίνακα"
                    className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] px-2 py-1 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A]">
                    <Upload className="h-3.5 w-3.5" /> Φόρτωση
                  </button>
                  <button data-testid={`bva-version-delete-${v.id}`} onClick={() => { if (window.confirm(`Διαγραφή της έκδοσης «${v.name}»;`)) del.mutate(v.id); }}
                    title="Διαγραφή" className="p-1 rounded-md text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
